// Lightweight fuzzy search — zero dependencies.
//
// Two-stage match:
//   1. Substring (case + accent + whitespace insensitive) → score 100
//   2. Subsequence — every char of the query appears in order in the
//      target, allowing gaps → score 50 + boost based on consecutive runs
//
// Returns matches sorted by score descending. Good enough for short
// label lists (~1k items) without pulling in fuse.js (~10KB).

/** Lowercase, strip accents, drop whitespace. */
export function normalize(str) {
  return String(str ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // diacritics
    .replace(/\s+/g, '');
}

/**
 * Score how well `query` matches `target`.
 * Returns -1 for no match, or a positive number where higher is better.
 */
export function fuzzyScore(query, target) {
  if (!query) return 0;
  const q = normalize(query);
  const t = normalize(target);
  if (!q || !t) return -1;

  // Substring: highest priority. Earlier match = better.
  const idx = t.indexOf(q);
  if (idx >= 0) return 1000 - idx;

  // Subsequence match: every char of q exists in t in order.
  let qi = 0;
  let consecutiveRun = 0;
  let bestRun = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      qi++;
      consecutiveRun++;
      if (consecutiveRun > bestRun) bestRun = consecutiveRun;
    } else {
      consecutiveRun = 0;
    }
  }
  if (qi < q.length) return -1;
  // All chars matched. Score blends "longer consecutive run is better"
  // with "shorter target is more specific".
  return 100 + bestRun * 10 - Math.max(0, t.length - q.length);
}

/**
 * Fuzzy-filter and sort a list of items.
 *
 * @param items   array of any
 * @param query   user's search text
 * @param keyFn   (item) => string OR (item) => string[]  (the haystack)
 * @param limit   max results (default 20)
 */
export function fuzzyFilter(items, query, keyFn, limit = 20) {
  if (!query || !query.trim()) return [];
  const scored = [];
  for (const item of items) {
    const keys = keyFn(item);
    const targets = Array.isArray(keys) ? keys : [keys];
    let best = -1;
    for (const t of targets) {
      const s = fuzzyScore(query, t);
      if (s > best) best = s;
    }
    if (best > 0) scored.push({ item, score: best });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(x => x.item);
}
